import { AlertCircle, Bell, Check, CheckCircle2, PackageCheck, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import type { ProjectNotification } from '@/types';
import { formatDateRelative, formatTime } from '@/lib/utils';

interface NotificationsPanelProps {
  notifications: ProjectNotification[];
  onClose: () => void;
  onMarkRead: (id: string) => void;
  onMarkAllRead: () => void;
}

export function NotificationsPanel({
  notifications,
  onClose,
  onMarkRead,
  onMarkAllRead,
}: NotificationsPanelProps) {
  const unreadCount = notifications.filter((notification) => !notification.read).length;

  const typeConfig = {
    status: { icon: Bell, badge: 'info' as const, label: 'Status' },
    review: { icon: AlertCircle, badge: 'warning' as const, label: 'Review' },
    delivery: { icon: PackageCheck, badge: 'success' as const, label: 'Delivery' },
    urgent: { icon: AlertCircle, badge: 'danger' as const, label: 'Urgent' },
    announcement: { icon: Bell, badge: 'neutral' as const, label: 'Announcement' },
  };

  return (
    <div className="absolute right-0 top-12 z-50 w-[min(92vw,420px)] rounded-lg border border-slate-200 bg-white shadow-xl">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <div>
          <h2 className="font-semibold text-slate-900">Project Alerts</h2>
          <p className="text-xs text-slate-500">{unreadCount} unread project notifications</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close notifications"
          className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="max-h-[420px] overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-slate-500">
            No project alerts yet.
          </div>
        ) : (
          notifications.map((notification) => {
            const config = typeConfig[notification.type];
            const Icon = config.icon;
            const timestamp = new Date(notification.timestamp);

            return (
              <div
                key={notification.id}
                className={`border-b border-slate-100 px-4 py-3 ${
                  notification.read ? 'bg-white' : 'bg-blue-50/60'
                }`}
              >
                <div className="flex gap-3">
                  <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-white text-blue-600 ring-1 ring-slate-200">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-medium text-slate-900">{notification.title}</h3>
                      <Badge variant={config.badge} size="sm">{config.label}</Badge>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">{notification.message}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-400">
                      {notification.taskTitle && <span>{notification.taskTitle}</span>}
                      <span>{formatDateRelative(timestamp)} at {formatTime(timestamp)}</span>
                    </div>
                  </div>
                  {!notification.read && (
                    <button
                      type="button"
                      onClick={() => onMarkRead(notification.id)}
                      aria-label={`Mark ${notification.title} as read`}
                      className="h-8 rounded-lg p-2 text-slate-400 transition-colors hover:bg-white hover:text-blue-600"
                    >
                      <Check className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <span className="text-xs text-slate-500">Updates are generated from active project status.</span>
        <Button
          size="sm"
          variant="outline"
          leftIcon={<CheckCircle2 className="h-4 w-4" />}
          onClick={onMarkAllRead}
          disabled={unreadCount === 0}
        >
          Mark all read
        </Button>
      </div>
    </div>
  );
}
